import { DataTable } from '@easydoctor/ui';
import type { TicketListScope, TicketQueueItem } from '@easyhelp/shared';
import useMediaQuery from '@mui/material/useMediaQuery';
import { RouterLink } from '../../components/RouterLink';
import { QUEUE_LINK_COLUMN, queueColumns } from './queueColumns';
import { ticketHref } from './queue-params';

interface QueueTableProps {
  /** Coda o archivio (#123): decide il link al dettaglio e il nome della tabella. */
  scope?: TicketListScope;
  items: readonly TicketQueueItem[];
  /** Primo caricamento: intestazione e righe skeleton della `DataTable`. */
  loading?: boolean;
  /** Codice del ticket aperto nel dettaglio (anche minuscolo, come può arrivare dall'URL). */
  currentCode: string | undefined;
  /** Query string corrente della coda, portata nel link al dettaglio. */
  search: string;
}

/** Attributo della riga con il codice del ticket: la pagina ci riporta il focus alla chiusura del dettaglio. */
export const TICKET_ROW_ATTRIBUTE = 'data-ticket-code';

/**
 * Tabella della coda (PRD-004 US-1, #105): `DataTable` di `@easydoctor/ui` senza contenitore proprio
 * (sta nella `MainCard` della pagina), con le colonne di {@link queueColumns}.
 *
 * Ogni riga è un link vero al dettaglio (`getRowHref` con il `RouterLink` della console) nella cella del
 * codice: si apre in una nuova scheda (Ctrl/Cmd o tasto centrale), si raggiunge con Tab e si apre con
 * Invio; il clic in un altro punto della riga segue lo stesso link. Il ticket aperto ha `aria-current`
 * sul link e la barra laterale (`currentRowId`).
 */
export function QueueTable({ scope = 'coda', items, loading = false, currentCode, search }: QueueTableProps) {
  const wide = useMediaQuery((theme) => theme.breakpoints.up('sm'), { noSsr: true });
  const current = currentCode?.toUpperCase();
  const currentRowId = current ? items.find((item) => item.code === current)?.id : undefined;
  return (
    <DataTable<TicketQueueItem>
      columns={queueColumns(new Date(), wide ? 'wide' : 'narrow')}
      data={[...items]}
      loading={loading}
      rowKey="id"
      getRowHref={(item) => ticketHref(item.code, search, scope)}
      LinkComponent={RouterLink}
      rowLinkColumnId={QUEUE_LINK_COLUMN}
      currentRowId={currentRowId}
      rowProps={(item) => ({ [TICKET_ROW_ATTRIBUTE]: item.code })}
      disableContainer
      aria-label={scope === 'coda' ? 'Ticket in coda' : 'Ticket in archivio'}
    />
  );
}
